import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Play } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { useCurrentIdentity } from '@/lib/identity-context';

interface TriggerScanButtonProps {
  siteId: string;
  size?: 'sm' | 'default';
}

export function TriggerScanButton({ siteId, size = 'sm' }: TriggerScanButtonProps) {
  const { hasPermission } = useCurrentIdentity();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST('/sites/{siteId}/crawls', {
        params: { path: { siteId } },
        body: {},
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (crawl) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.crawls(siteId) });
      navigate({ to: '/crawls/$crawlId', params: { crawlId: crawl.id } });
    },
  });

  if (!hasPermission('crawls:trigger')) {
    return null;
  }

  return (
    <Button
      size={size}
      variant="outline"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      data-testid={`trigger-scan-${siteId}`}
    >
      <Play className="h-3.5 w-3.5" />
      {mutation.isPending ? 'Queuing…' : 'Trigger scan'}
    </Button>
  );
}
